/**
 * Small geo helpers shared by the live-tracking surfaces.
 *
 * `Service` carries no latitude/longitude — only `locationAddress` (which is sometimes a
 * raw coordinate string) and `locationGoogleMapsLink` — so the destination pin has to be
 * recovered from those. The parsing primitives already existed in two places; they are
 * collected here so both the map and `GeocodedLocationLink` use the same ones.
 */

import { parseCoordinates } from '@/utils/orderSurveyDisplay'

export interface LatLng {
  lat: number
  lng: number
}

const EARTH_RADIUS_METERS = 6_371_008.8

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180
}

/** Great-circle distance in metres. Accurate enough for a "1.2 km away" line. */
export function haversineMeters(a: LatLng, b: LatLng): number {
  const dLat = toRadians(b.lat - a.lat)
  const dLng = toRadians(b.lng - a.lng)
  const lat1 = toRadians(a.lat)
  const lat2 = toRadians(b.lat)

  const h =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2)

  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(h))
}

function isValidLatLng(lat: number, lng: number): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
}

/**
 * Extract leading coordinates from a label like `"24.7513, 46.7089"` or the backend's
 * `"24.7513, 46.7089, -, - -"` (lat, lng, city, area with `-` placeholders). Reads the
 * first two comma-separated tokens and validates their ranges, so a real address that
 * merely starts with a number (e.g. `"7000 King Fahd Rd, Riyadh"`) is not misread.
 */
export function parseLeadingCoords(value: string): LatLng | null {
  const parts = value.split(',')
  if (parts.length < 2) return null
  const lat = Number(parts[0].trim())
  const lng = Number(parts[1].trim())
  if (!isValidLatLng(lat, lng)) return null
  return { lat, lng }
}

/** Pull coordinates out of a Google Maps deep link, if it carries any. */
export function parseCoordsFromMapsUrl(url: string | null | undefined): LatLng | null {
  if (!url) return null

  // `?q=lat,lng` and `/maps/search/?api=1&query=lat,lng`
  try {
    const params = new URL(url, window.location.origin).searchParams
    for (const key of ['q', 'query', 'destination', 'center'] as const) {
      const value = params.get(key)
      const coords = value ? parseLeadingCoords(value) : null
      if (coords) return coords
    }
  } catch {
    // not a parseable URL — fall through to the regex
  }

  // `/maps/@lat,lng,15z` and `/maps/dir/…/lat,lng`
  const match = url.match(/[@/](-?\d+\.\d+),\s*(-?\d+\.\d+)/)
  if (match) {
    const lat = Number(match[1])
    const lng = Number(match[2])
    if (isValidLatLng(lat, lng)) return { lat, lng }
  }

  return null
}

/**
 * Best effort at the coordinates of a service's location: the address field first
 * (it is frequently a coordinate string), then the maps deep link.
 */
export function parseServiceCoords(service: {
  locationAddress?: string | null
  locationGoogleMapsLink?: string | null
}): LatLng | null {
  const address = service.locationAddress
  if (address) {
    const fromAddress = parseCoordinates(address) ?? parseLeadingCoords(address)
    if (fromAddress) return fromAddress
  }
  return parseCoordsFromMapsUrl(service.locationGoogleMapsLink)
}
